import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LogOut, 
  User, 
  BookOpen, 
  Settings, 
  Menu, 
  X, 
  ArrowLeft, 
  Shield, 
  Coins, 
  Plus, 
  AlertTriangle,
  ChevronDown,
  Palette,
  Target,
  Zap,
  BarChart3,
  Home,
  Info,
  DollarSign,
  HelpCircle
} from 'lucide-react';

const Navbar = () => {
  const { user, logout, impersonating, originalAdmin, credits, refreshCredits } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = (dropdownName) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const stopImpersonating = async () => {
    try {
      // Get the original admin token
      const originalAdminToken = localStorage.getItem('original_admin_token');
      
      if (originalAdminToken) {
        // Use original admin token to stop impersonation
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stop-impersonate`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${originalAdminToken}` }
        });
        
        // Restore original admin token
        localStorage.setItem('token', originalAdminToken);
      }
      
      // Clear impersonation data
      localStorage.removeItem('impersonating');
      localStorage.removeItem('original_admin');
      localStorage.removeItem('original_admin_token');
      
      // Redirect back to admin
      window.location.href = '/admin';
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
      // Still clear data and redirect even if API call fails
      localStorage.removeItem('impersonating');
      localStorage.removeItem('original_admin');
      localStorage.removeItem('original_admin_token');
      window.location.href = '/login';
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* Impersonation Banner */}
      {impersonating && originalAdmin && (
        <div className="bg-yellow-500 text-black px-4 py-2 text-center text-sm font-medium">
          <div className="flex items-center justify-center space-x-2">
            <Shield className="h-4 w-4" />
            <span>
              You are impersonating <strong>{impersonating.full_name}</strong> ({impersonating.email})
            </span>
            <button
              onClick={stopImpersonating}
              className="ml-4 bg-black text-yellow-500 px-3 py-1 rounded text-xs hover:bg-gray-800 transition-colors flex items-center space-x-1"
            >
              <ArrowLeft className="h-3 w-3" />
              <span>Back to Admin</span>
            </button>
          </div>
        </div>
      )}
      
      <nav className="bg-gray-900/95 backdrop-blur-sm border-b border-purple-500/20 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:grid md:grid-cols-3 md:items-center" ref={dropdownRef}>
          {/* Logo */}
          <div className="flex justify-start md:justify-start">
            <a 
              href="/" 
              className="flex items-center space-x-2"
              onClick={(e) => {
                e.preventDefault();
                if (window.location.pathname === '/') {
                  // If already on homepage, scroll to top
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  // If on different page, navigate to homepage
                  window.location.href = '/';
                }
              }}
            >
              <img src="/logo.svg" alt="AI BookMoney" className="h-8 w-auto" />
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
                AI BookMoney
              </span>
            </a>
          </div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center justify-center">
            {user ? (
              <div className="flex items-center space-x-2">
                <Link 
                  to="/dashboard" 
                  className="text-gray-300 hover:text-purple-400 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-purple-500/10"
                >
                  Dashboard
                </Link>
                <Link 
                  to="/help" 
                  className="text-gray-300 hover:text-purple-400 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-purple-500/10 flex items-center space-x-1"
                >
                  <HelpCircle className="h-4 w-4" />
                  <span>Help</span>
                </Link>

                {/* Tools Dropdown */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDropdown('tools');
                    }}
                    className="text-gray-300 hover:text-purple-400 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-purple-500/10 flex items-center space-x-1"
                  >
                    <span>Tools</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === 'tools' ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {activeDropdown === 'tools' && (
                    <div 
                      className="absolute top-full left-0 mt-1 w-56 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="py-2">
                        <Link
                          to="/create"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveDropdown(null);
                          }}
                          className="flex items-center space-x-3 px-4 py-3 text-gray-300 hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                        >
                          <Zap className="h-4 w-4" />
                          <div>
                            <div className="font-medium">Book Creation Wizard</div>
                            <div className="text-xs text-gray-400">5-step guided workflow</div>
                          </div>
                        </Link>
                        <Link
                          to="/generate"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveDropdown(null);
                          }}
                          className="flex items-center space-x-3 px-4 py-3 text-gray-300 hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                        >
                          <BookOpen className="h-4 w-4" />
                          <div>
                            <div className="font-medium">Quick Generate</div>
                            <div className="text-xs text-gray-400">Direct book creation</div>
                          </div>
                        </Link>
                        <Link
                          to="/kdp-covers"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveDropdown(null);
                          }}
                          className="flex items-center space-x-3 px-4 py-3 text-gray-300 hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                        >
                          <Palette className="h-4 w-4" />
                          <div>
                            <div className="font-medium">Cover Generator</div>
                            <div className="text-xs text-gray-400">KDP & eBook covers</div>
                          </div>
                        </Link>
                        <Link
                          to="/metadata-optimizer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveDropdown(null);
                          }}
                          className="flex items-center space-x-3 px-4 py-3 text-gray-300 hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                        >
                          <Target className="h-4 w-4" />
                          <div>
                            <div className="font-medium">Metadata Optimizer</div>
                            <div className="text-xs text-gray-400">SEO & keywords</div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <a 
                  href="/" 
                  className="text-gray-300 hover:text-purple-400 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-purple-500/10 flex items-center space-x-1"
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.location.pathname === '/') {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.location.href = '/';
                    }
                  }}
                >
                  <Home className="h-4 w-4" />
                  <span>Home</span>
                </a>
                <Link
                  to="/kdp-guide"
                  className="text-gray-300 hover:text-purple-400 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-purple-500/10 flex items-center space-x-1 whitespace-nowrap"
                >
                  <BookOpen className="h-4 w-4" />
                  <span>KDP Guide</span>
                </Link>
                <a 
                  href="#features" 
                  className="text-gray-300 hover:text-purple-400 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-purple-500/10 flex items-center space-x-1"
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.location.pathname === '/') {
                      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      window.location.href = '/#features';
                    }
                  }}
                >
                  <Info className="h-4 w-4" />
                  <span>Features</span>
                </a>
                <a 
                  href="#pricing" 
                  className="text-gray-300 hover:text-purple-400 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-purple-500/10 flex items-center space-x-1"
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.location.pathname === '/') {
                      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      window.location.href = '/#pricing';
                    }
                  }}
                >
                  <DollarSign className="h-4 w-4" />
                  <span>Pricing</span>
                </a>
                <Link 
                  to="/login" 
                  className="text-gray-300 hover:text-purple-400 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-purple-500/10 whitespace-nowrap"
                >
                  Sign In
                </Link>
                <Link 
                  to="/register" 
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-colors whitespace-nowrap"
                >
                  Get Started
                </Link>
              </div>
            )}
          </div>

          {/* Right Side - Credits & User Menu */}
          <div className="hidden md:flex items-center justify-end space-x-2">
            {user && (
              <>
                {/* Credits Display */}
                <div className="flex items-center space-x-2 bg-gray-800/50 rounded-lg px-3 py-2 border border-gray-600">
                  <Coins className="h-4 w-4 text-yellow-400" />
                  <span className="text-sm font-medium text-white">{credits || 0}</span>
                  {credits !== null && credits < 10 && (
                    <AlertTriangle className="h-4 w-4 text-orange-400" />
                  )}
                </div>
                <Link
                  to="/credits/purchase"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:from-purple-700 hover:to-pink-700 transition-colors flex items-center space-x-1"
                >
                  <Plus className="h-4 w-4" />
                  <span>Top Up</span>
                </Link>

                {/* User Menu Dropdown */}
                <div className="relative">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleDropdown('user');
                    }}
                    className="text-gray-300 hover:text-purple-400 transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-purple-500/10 flex items-center space-x-2"
                  >
                    <User className="h-4 w-4" />
                    <span className="text-sm">{user?.full_name || 'User'}</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === 'user' ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {activeDropdown === 'user' && (
                    <div 
                      className="absolute top-full right-0 mt-1 w-48 bg-gray-800 border border-gray-600 rounded-lg shadow-xl z-50"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="py-2">
                        {user?.is_admin && (
                          <Link
                            to="/admin"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                            }}
                            className="flex items-center space-x-3 px-4 py-3 text-gray-300 hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                          >
                            <Settings className="h-4 w-4" />
                            <span>Admin Panel</span>
                          </Link>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLogout();
                            setActiveDropdown(null);
                          }}
                          className="w-full flex items-center space-x-3 px-4 py-3 text-gray-300 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <LogOut className="h-4 w-4" />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-300 hover:text-purple-400 transition-colors duration-200 p-2"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-800/95 backdrop-blur-sm border-b border-purple-500/20">
          <div className="px-4 py-6 space-y-4">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={closeMobileMenu}
                  className="flex items-center space-x-3 text-gray-300 hover:text-purple-400 transition-colors py-2"
                >
                  <BarChart3 className="h-5 w-5" />
                  <span>Dashboard</span>
                </Link>
                
                <Link
                  to="/kdp-guide"
                  onClick={closeMobileMenu}
                  className="flex items-center space-x-3 text-gray-300 hover:text-purple-400 transition-colors py-2 whitespace-nowrap"
                >
                  <BookOpen className="h-5 w-5" />
                  <span>KDP Guide</span>
                </Link>

                <Link
                  to="/help"
                  onClick={closeMobileMenu}
                  className="flex items-center space-x-3 text-gray-300 hover:text-purple-400 transition-colors py-2"
                >
                  <HelpCircle className="h-5 w-5" />
                  <span>Help</span>
                </Link>
                
                <div className="border-t border-gray-700 pt-4">
                  <div className="text-gray-400 text-sm font-medium mb-3">Tools</div>
                  <div className="space-y-2 ml-4">
                    <Link
                      to="/create"
                      onClick={closeMobileMenu}
                      className="flex items-center space-x-3 text-gray-300 hover:text-purple-400 transition-colors py-2"
                    >
                      <Zap className="h-4 w-4" />
                      <span>Book Creation Wizard</span>
                    </Link>
                    <Link
                      to="/generate"
                      onClick={closeMobileMenu}
                      className="flex items-center space-x-3 text-gray-300 hover:text-purple-400 transition-colors py-2"
                    >
                      <BookOpen className="h-4 w-4" />
                      <span>Quick Generate</span>
                    </Link>
                    <Link
                      to="/covers"
                      onClick={closeMobileMenu}
                      className="flex items-center space-x-3 text-gray-300 hover:text-purple-400 transition-colors py-2"
                    >
                      <Palette className="h-4 w-4" />
                      <span>Cover Generator</span>
                    </Link>
                    <Link
                      to="/metadata"
                      onClick={closeMobileMenu}
                      className="flex items-center space-x-3 text-gray-300 hover:text-purple-400 transition-colors py-2"
                    >
                      <Target className="h-4 w-4" />
                      <span>Metadata Optimizer</span>
                    </Link>
                  </div>
                </div>

                <div className="border-t border-gray-700 pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2 bg-gray-700/50 rounded-lg px-3 py-2">
                      <Coins className="h-4 w-4 text-yellow-400" />
                      <span className="text-sm font-medium text-white">{credits || 0} credits</span>
                    </div>
                    <Link
                      to="/credits/purchase"
                      onClick={closeMobileMenu}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-2 rounded-lg text-sm font-medium"
                    >
                      Top Up
                    </Link>
                  </div>
                </div>

                <div className="border-t border-gray-700 pt-4">
                  <div className="flex items-center space-x-3 text-gray-300 mb-4">
                    <User className="h-5 w-5" />
                    <span>{user?.full_name || 'User'}</span>
                  </div>
                  {user?.is_admin && (
                    <Link
                      to="/admin"
                      onClick={closeMobileMenu}
                      className="flex items-center space-x-3 text-gray-300 hover:text-purple-400 transition-colors py-2 ml-8"
                    >
                      <Settings className="h-4 w-4" />
                      <span>Admin Panel</span>
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-3 text-gray-300 hover:text-red-400 transition-colors py-2 ml-8 w-full text-left"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    closeMobileMenu();
                    if (window.location.pathname === '/') {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                      window.location.href = '/';
                    }
                  }}
                  className="flex items-center space-x-3 text-gray-300 hover:text-purple-400 transition-colors py-2"
                >
                  <Home className="h-5 w-5" />
                  <span>Home</span>
                </a>
                <Link
                  to="/kdp-guide"
                  onClick={closeMobileMenu}
                  className="flex items-center space-x-3 text-gray-300 hover:text-purple-400 transition-colors py-2 whitespace-nowrap"
                >
                  <BookOpen className="h-5 w-5" />
                  <span>KDP Guide</span>
                </Link>
                <a
                  href="#features"
                  onClick={(e) => {
                    e.preventDefault();
                    closeMobileMenu();
                    if (window.location.pathname === '/') {
                      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      window.location.href = '/#features';
                    }
                  }}
                  className="flex items-center space-x-3 text-gray-300 hover:text-purple-400 transition-colors py-2"
                >
                  <Info className="h-5 w-5" />
                  <span>Features</span>
                </a>
                <a
                  href="#pricing"
                  onClick={(e) => {
                    e.preventDefault();
                    closeMobileMenu();
                    if (window.location.pathname === '/') {
                      document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      window.location.href = '/#pricing';
                    }
                  }}
                  className="flex items-center space-x-3 text-gray-300 hover:text-purple-400 transition-colors py-2"
                >
                  <DollarSign className="h-5 w-5" />
                  <span>Pricing</span>
                </a>
                <div className="border-t border-gray-700 pt-4 space-y-2">
                  <Link
                    to="/login"
                    onClick={closeMobileMenu}
                    className="block text-center text-gray-300 hover:text-purple-400 transition-colors py-3 border border-gray-600 rounded-lg whitespace-nowrap"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    onClick={closeMobileMenu}
                    className="block text-center bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-medium whitespace-nowrap"
                  >
                    Get Started
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Navbar;